'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Calendar, QrCode, BarChart3 } from 'lucide-react';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { getPage, type Page } from '@/lib/cms';
import { sanitizeHtml } from '@/lib/sanitize-html';

const fallbackSteps = [
  { title: 'Organizer creates event', description: 'Create an event in minutes with essential details like title, dates, venue or online links, and capacity.' },
  { title: 'Adds ticket types', description: 'Configure free or paid tickets, quantities, pricing, and benefits for different attendee segments.' },
  { title: 'Publishes event', description: 'Publish the event and share the registration link with your audience via email, social media, or website embeds.' },
  { title: 'Users register', description: 'Attendees complete a simple registration flow, choose ticket types, and receive instant confirmation.' },
  { title: 'QR tickets generated', description: 'Each registration generates secure, unique QR tickets that can be stored in email or downloaded as PDF.' },
  { title: 'Check-in scanning', description: 'Staff scan QR tickets at entry gates using the EnthiosHub scanner to validate and check in attendees.' },
  { title: 'Organizer views analytics', description: 'Organizers track registrations, revenue, and check-ins in real time with dashboards and exports.' },
];

export default function HowItWorksPage() {
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPage('how-it-works').then((p) => {
      setPage(p ?? null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <MarketingLayout>
      <main className="py-8 pb-20 px-4 sm:px-6 lg:px-8">
        <section className="max-w-4xl mx-auto">
          {loading && <p className="text-surface-500 text-sm">Loading...</p>}
          {!loading && page?.content && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <h1 className="text-4xl md:text-5xl font-bold font-display mb-6">{page.title || 'How EnthiosHub Works'}</h1>
              <div className="prose prose-invert prose-surface max-w-none text-surface-400" dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.content) }} />
            </motion.div>
          )}
          {!loading && !page?.content && (
            <>
              <section className="text-center mb-16">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                  <h1 className="text-4xl md:text-5xl font-bold font-display mb-4">How EnthiosHub Works</h1>
                  <p className="text-lg text-surface-400 max-w-3xl mx-auto">A clear, end-to-end workflow for organizers and attendees.</p>
                </motion.div>
              </section>
              <section className="max-w-5xl mx-auto">
                <ol className="space-y-6">
                  {fallbackSteps.map((step, index) => (
                    <motion.li
                      key={step.title + index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                      className="relative pl-12 py-4 border-l border-surface-700/60"
                    >
                      <div className="absolute -left-3 top-5 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <span className="text-xs font-semibold text-text-inverse">{index + 1}</span>
                      </div>
                      <h2 className="text-lg font-semibold mb-1">{step.title}</h2>
                      <p className="text-surface-400 text-sm leading-relaxed">{step.description}</p>
                    </motion.li>
                  ))}
                </ol>
                <div className="mt-16 grid md:grid-cols-3 gap-6">
                  <div className="p-6 bg-surface-800/60 rounded-2xl border border-surface-700">
                    <Calendar className="w-6 h-6 text-primary-400 mb-3" />
                    <h3 className="font-semibold mb-1">Before the Event</h3>
                    <p className="text-surface-400 text-sm">Configure events, tickets, and registration flows in advance.</p>
                  </div>
                  <div className="p-6 bg-surface-800/60 rounded-2xl border border-surface-700">
                    <QrCode className="w-6 h-6 text-primary-400 mb-3" />
                    <h3 className="font-semibold mb-1">During the Event</h3>
                    <p className="text-surface-400 text-sm">Scan QR tickets at entry, manage check-ins, and keep queues moving.</p>
                  </div>
                  <div className="p-6 bg-surface-800/60 rounded-2xl border border-surface-700">
                    <BarChart3 className="w-6 h-6 text-primary-400 mb-3" />
                    <h3 className="font-semibold mb-1">After the Event</h3>
                    <p className="text-surface-400 text-sm">Review analytics, export reports, and use insights to improve.</p>
                  </div>
                </div>
                <div className="mt-12 flex items-center justify-center gap-3 text-sm text-surface-400">
                  <CheckCircle2 className="w-4 h-4 text-secondary-400" />
                  <span>Fully powered by the EnthiosHub admin panel and APIs</span>
                </div>
              </section>
            </>
          )}
        </section>
      </main>
    </MarketingLayout>
  );
}
